<template>
  <b-col v-if="isSalesManager" cols="6" md="3" lg="2">
    <b-card
      class="text-center cursor-pointer boxes sales-manager-cp-card"
      @click="openModal"
    >
      <b-avatar class="mb-1" variant="light-success" size="35">
        <feather-icon icon="UsersIcon" size="17" />
      </b-avatar>
      <div class="truncate">
        <h2 class="mb-25 font-weight-bolder sales-manager-cp-count">
          {{ loading && !fetched ? "—" : cpCount }}
        </h2>
        <span class="sales-manager-cp-label">Company Associate Partners Created</span>
      </div>
    </b-card>

    <b-modal
      v-model="modalVisible"
      size="lg"
      title="Company Associate Partners You Created"
      cancel-title="Close"
      hide-footer
      scrollable
      modal-class="sales-manager-cp-modal"
    >
      <div v-if="loading" class="text-center py-3">
        <b-spinner variant="primary" />
        <p class="text-muted mb-0 mt-1">Loading company associate partners...</p>
      </div>
      <div v-else-if="!companyAssociatePartners.length" class="text-center text-muted py-3">
        <feather-icon icon="UsersIcon" size="48" class="mb-1 text-secondary" />
        <p class="mb-0">You have not created any company associate partners yet.</p>
      </div>
      <div v-else>
        <p class="text-muted mb-1">
          {{ cpCount }} company associate partner{{ cpCount === 1 ? "" : "s" }} — click a row to open details.
        </p>
        <b-table
          :items="companyAssociatePartners"
          :fields="tableFields"
          small
          striped
          hover
          responsive
          class="mb-0"
          tbody-tr-class="cursor-pointer"
          @row-clicked="onRowClick"
        >
          <template #cell(name)="data">
            <span class="font-weight-bold">{{ formatName(data.item) }}</span>
          </template>
          <template #cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'light-success' : 'light-danger'">
              {{ data.item.is_active ? "Active" : "Inactive" }}
            </b-badge>
          </template>
        </b-table>
      </div>
    </b-modal>
  </b-col>
</template>

<script>
import {
  BCol,
  BCard,
  BAvatar,
  BBadge,
  BModal,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import axios from "@/components/axios";

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}");
  } catch (e) {
    return {};
  }
}

function isSalesManagerPosition(positionName) {
  if (!positionName) return false;
  const normalized = String(positionName).toLowerCase().replace(/\s+/g, "");
  return normalized === "salesmanager" || normalized.includes("salesmanager");
}

export default {
  name: "SalesManagerCreatedCpCard",
  components: {
    BCol,
    BCard,
    BAvatar,
    BBadge,
    BModal,
    BSpinner,
    BTable,
  },
  data() {
    return {
      userData: readUserData(),
      accessToken: localStorage.getItem("accessToken"),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      cpCount: 0,
      companyAssociatePartners: [],
      loading: false,
      modalVisible: false,
      fetched: false,
      tableFields: [
        { key: "name", label: "Name" },
        { key: "username", label: "Username" },
        { key: "mobile", label: "Mobile" },
        { key: "email", label: "Email" },
        { key: "joiningdate", label: "Joined On" },
        { key: "is_active", label: "Status" },
      ],
    };
  },
  computed: {
    isSalesManager() {
      const positionName =
        (this.userData.position && this.userData.position.name) ||
        this.userData.position_name ||
        "";
      return isSalesManagerPosition(positionName);
    },
  },
  mounted() {
    if (this.isSalesManager) {
      this.fetchCreatedCompanyAssociatePartners();
    }
  },
  methods: {
    formatName(cp) {
      if (cp.fullname) return cp.fullname;
      return [cp.name, cp.surname].filter(Boolean).join(" ").trim() || "—";
    },
    onRowClick(item) {
      this.goToCp(item.id);
    },
    async fetchCreatedCompanyAssociatePartners() {
      this.loading = true;
      try {
        const response = await axios({
          method: "GET",
          url: `${this.baseApi}/sales-manager-created-channel-partners`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
        const payload = response.data.data || {};
        this.cpCount = payload.count ?? 0;
        this.companyAssociatePartners = (Array.isArray(payload.channel_partners)
          ? payload.channel_partners
          : []
        ).map((cp) => ({
          ...cp,
          joiningdate: cp.joiningdate || cp.enterdatetime || "—",
        }));
        this.fetched = true;
      } catch (error) {
        console.error("Failed to load sales manager company associate partners:", error);
        this.cpCount = 0;
        this.companyAssociatePartners = [];
      } finally {
        this.loading = false;
      }
    },
    openModal() {
      this.modalVisible = true;
      if (!this.fetched) {
        this.fetchCreatedCompanyAssociatePartners();
      }
    },
    goToCp(id) {
      if (!id) return;
      this.modalVisible = false;
      this.$router.push(`/master/crm/editcompanyassociatepartner/${id}`);
    },
  },
};
</script>

<style scoped>
.sales-manager-cp-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sales-manager-cp-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.12);
}

.sales-manager-cp-count {
  font-size: 1.5rem;
  line-height: 1.2;
}

.sales-manager-cp-label {
  display: block;
  font-size: 0.78rem;
  line-height: 1.25;
  color: #6e6b7b;
}
</style>
